import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Briefcase, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useIsMobile } from '../../hooks/useIsMobile';

const G = '#0F5A3A';
const GOLD = '#F5B327';
const IVORY = '#F4F0E6';
const BTN_R = '8px';

const BRIEFCASE_IMG = 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=700&auto=format&fit=crop&q=80';

const CATEGORIES = [
  { title: 'Internships', desc: 'Gain real-world experience and build your resume with vetted opportunities from top employers.', color: G, icon: <Briefcase size={18} color="#fff" /> },
  { title: 'First Jobs', desc: 'Find entry-level roles and launch your career with confidence. Curated for recent graduates.', color: '#1d4ed8', icon: <ArrowRight size={18} color="#fff" /> },
  { title: 'Skills Development', desc: 'Build in-demand skills through courses, certifications, and practical workshops.', color: '#b45309', icon: <Clock size={18} color="#fff" /> },
];

export const FirstBriefcase = () => {
  const isMobile = useIsMobile();

  return (
    <section style={{ background: IVORY, padding: isMobile ? '48px 0' : '72px 0' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: isMobile ? '0 20px' : '0 40px' }}>
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55 }}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end', flexDirection: isMobile ? 'column' : 'row', marginBottom: isMobile ? '28px' : '40px', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: GOLD, marginBottom: '8px' }}>First Briefcase</div>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: isMobile ? '1.75rem' : 'clamp(1.8rem, 3vw, 2.6rem)', fontWeight: 900, color: G, lineHeight: 1.1, letterSpacing: '-0.02em', margin: 0 }}>
              Build experience.<br />Launch your future.
            </h2>
          </div>
          <Link to="/first-briefcase" data-testid="first-briefcase-cta"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '12px 22px', background: G, color: '#fff', fontWeight: 700, fontSize: '13px', borderRadius: BTN_R, textDecoration: 'none', flexShrink: 0 }}>
            Explore First Briefcase <ArrowRight size={14} />
          </Link>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1.2fr 1fr 1fr 1fr', gap: '14px' }}>
          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            style={{ borderRadius: '14px', overflow: 'hidden', position: 'relative', minHeight: isMobile ? '180px' : '280px', background: '#1a1a1a', boxShadow: '0 12px 40px rgba(0,0,0,0.1)', gridColumn: isMobile ? 'span 2' : 'span 1' }}>
            <img src={BRIEFCASE_IMG} alt="Young professional" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.65 }} />
            <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to top, ${G}e0 0%, transparent 55%)` }} />
            <div style={{ position: 'absolute', bottom: '20px', left: '20px', right: '20px', zIndex: 2 }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: GOLD, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px' }}>Your First Chapter</div>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: isMobile ? '1rem' : '1.2rem', fontWeight: 900, color: '#fff', lineHeight: 1.2, marginBottom: '10px' }}>Land your first real opportunity.</div>
              <Link to="/first-briefcase" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 700, color: GOLD, textDecoration: 'none' }}>Browse <ArrowRight size={12} /></Link>
            </div>
          </motion.div>

          {CATEGORIES.map((cat, i) => (
            <motion.div key={cat.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.55, delay: (i + 1) * 0.08 }}
              style={{ background: '#fff', borderRadius: '14px', padding: isMobile ? '18px' : '24px', border: '1px solid #e8e4df', boxShadow: '0 4px 20px rgba(15,90,58,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: isMobile ? '160px' : '200px' }}>
              <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: cat.color, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>{cat.icon}</div>
              <div>
                <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: isMobile ? '1rem' : '1.1rem', fontWeight: 800, color: G, marginBottom: '6px' }}>{cat.title}</h3>
                {!isMobile && <p style={{ fontSize: '13px', color: '#6E6A61', lineHeight: 1.65, marginBottom: '14px' }}>{cat.desc}</p>}
              </div>
              <Link to="/first-briefcase" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 700, color: G, textDecoration: 'none' }}>Explore <ArrowRight size={12} /></Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FirstBriefcase;
